/* eslint-disable sonarjs/no-duplicate-string */
import { useState } from 'react';
import dynamic from 'next/dynamic';
import { Form, Formik } from 'formik';
import { array, object, string } from 'yup';

import { Container, NextLink, Text } from '@/atoms';
import { Button, Checkbox, Input, TextArea } from '@/molecules';
import { FlexibleContent, HeroSlider } from '@/templates';
import { getBaseLayout } from '@/layouts/BaseLayout';

import { getInitialProps } from '@/lib/graphql/api';
import {
  FlexibleContentBlocks,
  pagePropsPartial,
  seo,
  sliderHero,
} from '@/lib/graphql/partials';
import { PageQuery } from '@/lib/graphql/querys';
import { pagePropsServerTransformer } from '@/lib/graphql/transformers';
import { routes } from '@/lib/routes';
import { addFormData, sendCF7Form } from '@/lib/utils';
import { config } from '@/config';

import type { GetStaticPropsResult } from 'next';
import type { NextPageWithLayout } from 'pages/_app';

const Ship = dynamic(() => import('@/assets/images/coming-soon/ship.svg'), {
  ssr: false,
});

const Beer = dynamic(() => import('@/assets/images/coming-soon/beer.svg'), {
  ssr: false,
});

interface RegalosEmpresa {
  page: {
    seo: any;
    hero: any;
    flexibleContent: {
      firstFlexibleContent: any[];
      restFlexibleContent: any[];
    };
  };
}

const FORM_ID = {
  name: 'regalos-empresa-name',
  email: 'regalos-empresa-email',
  company: 'regalos-empresa-company',
  phone: 'regalos-empresa-phone',
  description: 'regalos-empresa-description',
  acepto: 'regalos-empresa-accept-terms',
};

const INITIAL_VALUES = {
  'regalos-empresa-name': '',
  'regalos-empresa-email': '',
  'regalos-empresa-company': '',
  'regalos-empresa-phone': '',
  'regalos-empresa-description': '',
  'regalos-emoresa-accept-terms': [],
};

const FORM_SCHEMA = object().shape({
  'regalos-empresa-name': string().required('El campo es obligatorio'),
  'regalos-empresa-email': string()
    .email('Formato de email inválido')
    .required('El campo es obligatorio'),
  'regalos-empresa-company': string().required('El campo es obligatorio'),
  'regalos-empresa-phone': string()
    .required('El campo es obligatorio')
    .matches(/[0-9]/, 'El valor tiene que ser numérico')
    .length(9, 'Longitud no válida'),
  'regalos-empresa-description': string().required('El campo es obligatorio'),
  'regalos-empresa-accept-terms': array()
    .required('Acepta la política de privacidad y cookies')
    .min(1, 'Acepta la política de privacidad y cookies'),
});

const RegalosEmpresa: NextPageWithLayout<RegalosEmpresa> = ({ page }) => {
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [error, setError] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  if (formSubmitted) {
    return (
      <div className="flex flex-wrap items-center justify-center gap-6 py-16 md:gap-11">
        {error ? (
          <>
            <Ship className="w-[300px]" />
            <div className="flex max-w-[400px] flex-col items-center gap-4 text-center md:items-start md:text-left">
              <h3 className="u-display u-display--s">¡Oops!</h3>
              <span>{message}</span>
              <Button
                size="normal"
                className="max-w-[inherit]"
                onClick={() => {
                  setError(false);
                  setFormSubmitted(false);
                }}
              >
                VOLVER al formulario
              </Button>
            </div>
          </>
        ) : (
          <>
            <Beer className="w-[120px]" />
            <div className="flex max-w-[400px] flex-col items-center gap-4 text-center md:items-start md:text-left">
              <h3 className="u-display u-display--s">
                ¡gracias por ESCRIBIRNOS!
              </h3>
              <span>{message}</span>
              <NextLink href={routes.index} passHref>
                <Button as="a" size="normal">
                  VOLVER A LA TIENDA
                </Button>
              </NextLink>
            </div>
          </>
        )}
      </div>
    );
  }

  return (
    <>
      <HeroSlider hero={page.hero} />
      <FlexibleContent
        type="Page"
        flexible={page.flexibleContent.firstFlexibleContent}
      />
      <Container wrapper id="contact_form">
        <h2>Contacta con nosotros</h2>

        <Formik
          initialValues={INITIAL_VALUES}
          validationSchema={FORM_SCHEMA}
          onSubmit={(formValues) => {
            if (!config.forms.companyGifts) {
              setError(true);
              setMessage('Algo ha salido mal.');
              return;
            }

            return sendCF7Form({
              id: config.forms.companyGifts,
              data: addFormData(FORM_ID, formValues),
            })
              .then((value) => {
                setFormSubmitted(true);
                setMessage(value.message);
              })
              .catch((error) => {
                setFormSubmitted(true);
                setError(true);
                setMessage(error.message);
              })
              .finally(() => {
                window.scrollTo({
                  top: 0,
                  behavior: 'instant',
                });
              });
          }}
        >
          {({ errors, isSubmitting, isValid }) => (
            <Form noValidate className="mt-4 space-y-4">
              <Input
                id={FORM_ID.name}
                name={FORM_ID.name}
                placeholder="Nombre *"
                error={errors['regalos-empresa-name']}
              />

              <Input
                id={FORM_ID.email}
                name={FORM_ID.email}
                type="email"
                placeholder="Email *"
                error={errors['regalos-empresa-email']}
              />

              <Input
                id={FORM_ID.company}
                name={FORM_ID.company}
                placeholder="Empresa *"
                error={errors['regalos-empresa-company']}
              />

              <Input
                id={FORM_ID.phone}
                name={FORM_ID.phone}
                placeholder="Nº de teléfono *"
                error={errors['regalos-empresa-phone']}
              />

              <TextArea
                id={FORM_ID.description}
                name={FORM_ID.description}
                placeholder="¿En qué estás interesado? *"
                rows={7}
                error={errors['regalos-empresa-description']}
              />

              <span className="u-body u-body--s mb-6 inline-block text-typo-tertiary">
                *Campos obligatorios
              </span>

              <Text className="u-headline u-headline--h4 mt-8">
                Información básica privacidad
              </Text>

              <Text className="u-body u-body--s text-typo-tertiary">
                Responsable del Tratamiento: Hijos de Rivera S.A.U.; finalidad
                del tratamiento: gestión de la consulta; legitimación del
                tratamiento: ejecución de un contrato/acuerdo; destinatarios: no
                se cederán datos, salvo obligación legal; derechos: acceder,
                rectificar y suprimir los datos, así como otros derechos, como
                se explica en la política de privacidad. Más información en la{' '}
                <NextLink href={routes.legalNoticePrivacyPolicy}>
                  <a className="u-body u-body--xs text-secondary-500">
                    política de privacidad y cookies.
                  </a>
                </NextLink>
              </Text>

              <Checkbox
                id={FORM_ID.acepto}
                name={FORM_ID.acepto}
                className="u-label u-label--s mt-[19px]"
                values={[
                  {
                    value: 'true',
                    label: (
                      <p className="max-w-[310px]">
                        Acepto la{' '}
                        <NextLink href={routes.legalNoticePrivacyPolicy}>
                          <a className="text-secondary-500">
                            política de privacidad y cookies
                          </a>
                        </NextLink>
                      </p>
                    ),
                  },
                ]}
                error={errors[FORM_ID.acepto as keyof typeof errors]}
              />

              <Button
                variant="tertiary"
                size="large"
                type="submit"
                className="!text-white"
                disabled={isSubmitting || !isValid}
                loading={isSubmitting}
              >
                enviar
              </Button>
            </Form>
          )}
        </Formik>
      </Container>
      <FlexibleContent
        type="Page"
        flexible={page.flexibleContent.restFlexibleContent}
      />
    </>
  );
};

export const getStaticProps = async (): Promise<GetStaticPropsResult<any>> => {
  const content = `
        page(id: $slug, idType: URI) {
            ${seo}
            hero: ${sliderHero('Page')}
            flexibleContent: ${FlexibleContentBlocks('Page')}
        }
    `;

  const query = PageQuery(pagePropsPartial, content);

  const data = await pagePropsServerTransformer({
    staticProps: await getInitialProps(query, {
      slug: '/regalos-empresa-clientes-proveedores-empleados',
    }),
    type: 'Page',
    path: '/regalos-empresa-clientes-proveedores-empleados',
  });

  if (data.notFound) return { notFound: true };

  const firstFlexibleContent =
    data?.props?.page?.flexibleContent?.flexible?.slice(0, 1) || [];
  const restFlexibleContent =
    data?.props?.page?.flexibleContent?.flexible?.slice(1) || [];

  return {
    props: {
      type: 'Page',
      ...data.props,
      page: {
        ...data.props.page,
        flexibleContent: {
          firstFlexibleContent: firstFlexibleContent,
          restFlexibleContent: restFlexibleContent,
        },
      },
    },
    ...(process.env.REVALIDATE && {
      revalidate: parseInt(process.env.REVALIDATE),
    }),
  };
};

RegalosEmpresa.getLayout = getBaseLayout;

export default RegalosEmpresa;
